import { config } from 'src/utils/config';
import { getStandardHeaders, performFetchWithLogging } from '../utils/helpers';
import {
  TeleportListResponse,
  TeleportLatestStatsResponse,
  TeleportStatsResponse,
  TeleportStatsQueryParams,
} from 'src/types/TTeleportStats';

/**
 * List all teleport installations
 * GET /api/v1/teleport-stats/teleports
 */
export async function getTeleportList(
  accessToken: string,
  userId?: number | null,
  teleportIdPrefix?: string,
  totalAssets?: number | null,
  cpuCores?: number | null,
  orderBy?: 'total_assets' | 'cpu_cores' | 'last_seen',
  order?: 'asc' | 'desc',
): Promise<TeleportListResponse> {
  const queryParams = new URLSearchParams();

  if (userId) {
    queryParams.append('user_id', userId.toString());
  }

  if (teleportIdPrefix) {
    queryParams.append('teleport_id_prefix', teleportIdPrefix);
  }

  if (totalAssets !== undefined && totalAssets !== null) {
    queryParams.append('total_assets', totalAssets.toString());
  }

  if (cpuCores !== undefined && cpuCores !== null) {
    queryParams.append('cpu_cores', cpuCores.toString());
  }

  if (orderBy) {
    queryParams.append('order_by', orderBy);
  }

  if (order) {
    queryParams.append('order', order);
  }

  const queryString = queryParams.toString();
  const url = `${config.hostUrl}/api/v1/teleport-stats/teleports${queryString ? `?${queryString}` : ''}`;

  try {
    return await performFetchWithLogging(
      url,
      {
        method: 'GET',
        headers: { ...getStandardHeaders(accessToken) },
        credentials: 'include',
      },
      'json',
    ) as TeleportListResponse;
  } catch (error) {
    throw error;
  }
}

/**
 * Get latest stats for each teleport installation
 * GET /api/v1/teleport-stats/latest
 */
export async function getTeleportLatestStats(
  accessToken: string,
  teleportId?: string,
  userId?: number | null,
): Promise<TeleportLatestStatsResponse> {
  const queryParams = new URLSearchParams();

  if (teleportId) {
    queryParams.append('teleport_id', teleportId);
  }

  if (userId) {
    queryParams.append('user_id', userId.toString());
  }

  const queryString = queryParams.toString();
  const url = `${config.hostUrl}/api/v1/teleport-stats/latest${queryString ? `?${queryString}` : ''}`;

  try {
    return await performFetchWithLogging(
      url,
      {
        method: 'GET',
        headers: { ...getStandardHeaders(accessToken) },
        credentials: 'include',
      },
      'json',
    ) as TeleportLatestStatsResponse;
  } catch (error) {
    throw error;
  }
}

/**
 * Query teleport statistics with optional filtering
 * GET /api/v1/teleport-stats
 */
export async function getTeleportStats(
  accessToken: string,
  params: TeleportStatsQueryParams = {},
): Promise<TeleportStatsResponse> {
  const queryParams = new URLSearchParams();

  if (params.teleport_id) {
    queryParams.append('teleport_id', params.teleport_id);
  }

  if (params.user_id) {
    queryParams.append('user_id', params.user_id);
  }

  if (params.from) {
    queryParams.append('from', params.from);
  }

  if (params.to) {
    queryParams.append('to', params.to);
  }

  if (params.aggregated_only !== undefined) {
    queryParams.append('aggregated_only', params.aggregated_only.toString());
  }

  if (params.limit !== undefined) {
    queryParams.append('limit', params.limit.toString());
  }

  if (params.offset !== undefined) {
    queryParams.append('offset', params.offset.toString());
  }

  const queryString = queryParams.toString();
  const url = `${config.hostUrl}/api/v1/teleport-stats${queryString ? `?${queryString}` : ''}`;

  try {
    return await performFetchWithLogging(
      url,
      {
        method: 'GET',
        headers: { ...getStandardHeaders(accessToken) },
        credentials: 'include',
      },
      'json',
    ) as TeleportStatsResponse;
  } catch (error) {
    throw error;
  }
}
